import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { useSession, OFFICE_ROLE_LABEL, type OfficeRole } from "@/lib/session";
import { useShop, useTill } from "@/lib/till-context";
import { atLeast, type Item } from "@/lib/types";
import { mediaUrl } from "@/lib/media";
import { Avatar, cx } from "@/components/ui";
import { Icon } from "@/components/Icon";
import { useInstall } from "@/lib/install";
import { session } from "@/lib/session";

type NavItem = { to: string; label: string; icon: string; min?: "manager"; count?: "products"; /** Back office roles that see it; none = everyone. */ roles?: OfficeRole[] };
const GROUPS: { label: string; items: NavItem[] }[] = [
  { label: "Overview", items: [{ to: "/", label: "Dashboard", icon: "dash" }, { to: "/reports", label: "Reports", icon: "report" }] },
  { label: "Stock", items: [{ to: "/products", label: "Products & stock", icon: "box", count: "products" }, { to: "/health", label: "Stock health", icon: "dash" }, { to: "/stock", label: "Receive & transfer", icon: "truck", roles: ["owner", "gm", "manager", "stock"] }] },
  { label: "Customers", items: [{ to: "/customers", label: "Customers & groups", icon: "team", roles: ["owner", "gm", "manager", "accountant"] }, { to: "/owed", label: "Money owed", icon: "report", roles: ["owner", "gm", "manager", "accountant"] }] },
  { label: "Team & setup", items: [{ to: "/staff", label: "Staff & roles", icon: "team", roles: ["owner", "gm", "manager"] }, { to: "/team", label: "Team (back office)", icon: "team", roles: ["owner", "gm"] }, { to: "/settings", label: "Settings & tills", icon: "gear", roles: ["owner", "gm", "manager"] }] },
];
const TITLES: Record<string, string> = { "/": "Dashboard", "/reports": "Reports", "/products": "Products & stock", "/stock": "Receive & transfer", "/customers": "Customers & groups", "/staff": "Staff & roles", "/team": "Team", "/owed": "Money owed", "/health": "Stock health","/settings": "Settings & tills" };

/**
 * The back office shell (its own site, signed in with a store admin account). Wide screens: full sidebar. Tablets (md to xl): an icon strip.
 * Phones: a top bar with a slide-out menu.
 */
export function Layout() {
  const loc = useLocation();
  const [drawer, setDrawer] = useState(false);
  useEffect(() => setDrawer(false), [loc.pathname]);
  return (
    <div className="flex min-h-full bg-slate-50">
      <aside className="sticky top-0 hidden h-screen flex-none md:flex">
        <div className="h-full xl:hidden">
          <Sidebar variant="rail" />
        </div>
        <div className="hidden h-full xl:block">
          <Sidebar variant="full" />
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 bg-slate-900 px-2 text-white md:hidden">
          <button type="button" aria-label="Open menu" onClick={() => setDrawer(true)} className="grid h-11 w-11 place-items-center">
            <Icon name="menu" size={20} />
          </button>
          <b className="min-w-0 flex-1 truncate text-[15px]">{TITLES[loc.pathname] ?? "Back office"}</b>
        </header>
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      {drawer ? (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <button type="button" aria-label="Close menu" onClick={() => setDrawer(false)} className="absolute inset-0 bg-slate-900/50" />
          <Sidebar variant="drawer" className="absolute inset-y-0 left-0 w-[86vw] max-w-[320px]" onClose={() => setDrawer(false)} />
        </div>
      ) : null}
    </div>
  );
}

function Sidebar({ variant, className, onClose }: { variant: "full" | "rail" | "drawer"; className?: string; onClose?: () => void }) {
  const s = useSession();
  const { store, me } = useTill();
  const role: OfficeRole = me?.user.role ?? s.user?.role ?? "owner";
  const shop = useShop();
  const app = useInstall();

  const rail = variant === "rail";
  const products = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: Item[] }>(till("/admin/items")), staleTime: 60_000 });
  const count = products.data?.items.filter((i) => i.active).length;
  const name = store?.storeName ?? "Store";
  // The brand's compact logo (no tagline) and mark when the site ships them, else the store's logo.
  const brand = import.meta.env.VITE_BRAND as string | undefined;
  const logo = brand ? `${brand}/logo-compact.png` : store?.settings?.logo;
  return (
    <nav aria-label="Back office" className={cx("ui-side flex h-full flex-col bg-graphite text-white", rail ? "w-[72px] items-center px-0 py-3.5" : variant === "full" ? "w-[248px] px-3.5 py-4" : "px-3.5 py-4", className)}>
      {/* Store */}
      <div className={cx("flex items-center gap-3", rail ? "mb-3" : "border-b border-slate-800 px-1.5 pb-4")}>
        {rail && brand ? (
          <img src={`${brand}/mark.png`} alt={name} title={name} className="h-10 w-10 flex-none bg-white object-contain p-1" />
        ) : rail || !logo ? (
          <span className="grid h-10 w-10 flex-none place-items-center bg-brand font-black" title={rail ? name : undefined}>
            {name.trim()[0]?.toUpperCase()}
          </span>
        ) : null}
        {!rail ? (
          <span className="flex min-w-0 flex-1 flex-col gap-1.5">
            {logo ? <img src={brand ? logo : mediaUrl(logo, 600)} alt={name} className="h-7 w-auto max-w-[180px] object-contain object-left" style={brand ? undefined : { filter: "brightness(0) invert(1)" }} /> : <b className="line-clamp-2 text-[13px] leading-tight tracking-wide">{name}</b>}
            <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-slate-400">Shop back office</span>
          </span>
        ) : null}
        {onClose ? (
          <button type="button" aria-label="Close menu" onClick={onClose} className="grid h-10 w-10 flex-none place-items-center text-slate-300">
            <Icon name="x" />
          </button>
        ) : null}
      </div>

      {/* Sections */}
      <div className={cx("flex flex-col", rail ? "mt-2 items-center gap-1.5" : "")}>
        {GROUPS.map((g) => {
          const items = g.items.filter((i) => (!i.min || atLeast(s.staff?.role, i.min)) && (!i.roles || i.roles.includes(role))).map((i) => (i.to === "/stock" && !shop.website ? { ...i, label: "Receive & count stock" } : i));
          if (!items.length) return null;
          return (
            <div key={g.label} className={cx("flex flex-col", rail ? "items-center gap-1.5" : "gap-0.5")}>
              {rail ? <span className="my-1.5 h-px w-8 bg-slate-800" /> : <span className="mx-2.5 mb-1.5 mt-5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{g.label}</span>}
              {items.map((i) => (
                <NavLink
                  key={i.to}
                  to={i.to}
                  end={i.to === "/"}
                  title={rail ? i.label : undefined}
                  aria-label={rail ? i.label : undefined}
                  className={({ isActive }) => cx("ui-navlink flex items-center gap-2.5 text-sm", rail ? "h-12 w-12 justify-center" : "h-[42px] px-3", isActive ? "ui-navlink-on border-l-4 border-brand-light bg-slate-900 font-semibold text-white" : "border-l-4 border-transparent text-slate-300 hover:bg-slate-800 hover:text-white")}
                >
                  {({ isActive }) => (
                    <>
                      <Icon name={i.icon} size={rail ? 20 : 18} />
                      {!rail ? <span className="min-w-0 flex-1 truncate">{i.label}</span> : null}
                      {!rail && i.count && count != null ? <span className={cx("px-1.5 font-mono text-[11px]", isActive ? "bg-brand-dark" : "bg-slate-800")}>{count}</span> : null}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </div>

      {/* Install as an app (Chrome / Edge) */}
      {app.canPrompt && !rail ? (
        <button type="button" onClick={() => void app.install()} className="mx-1.5 mt-auto flex h-10 items-center justify-center gap-2 bg-brand text-[13px] font-bold text-white">
          Install as an app
        </button>
      ) : null}

      {/* Signed in */}
      <div className={cx(app.canPrompt && !rail ? "mt-3" : "mt-auto", "flex items-center gap-2.5", rail ? "flex-col pt-3" : "border-t border-slate-800 px-1.5 pt-3")}>
        <span title={rail ? `${s.user?.name} · ${s.user?.email}` : undefined}>
          <Avatar name={s.user?.name ?? "?"} color="#9fe0e3" size={34} />
        </span>
        {!rail ? (
          <span className="flex min-w-0 flex-1 flex-col">
            <b className="truncate text-[13px]">{s.user?.name}</b>
            <span className="truncate text-[11px] text-slate-400">{OFFICE_ROLE_LABEL[role]} · {s.user?.email}</span>
          </span>
        ) : null}
        <button type="button" aria-label="Sign out" title="Sign out" onClick={() => session.signOut()} className="grid h-9 w-9 flex-none place-items-center bg-slate-800 text-slate-300 hover:text-white">
          <Icon name="lock" size={16} />
        </button>
      </div>
    </nav>
  );
}

export function PageHead({ title, sub, children }: { title: string; sub?: string; children?: React.ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="ui-pagetitle text-3xl font-black tracking-tight sm:text-[34px]">{title}</h1>
        {sub ? <p className="mt-1 text-sm text-slate-500">{sub}</p> : null}
      </div>
      {children ? <div className="flex flex-wrap gap-2">{children}</div> : null}
    </div>
  );
}

export function Card({ title, right, children, className }: { title?: string; right?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cx("border border-slate-200 bg-white", className)}>
      {title ? (
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pb-2 pt-4 sm:px-5">
          <h2 className="text-base font-bold">{title}</h2>
          {right}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export const th = "whitespace-nowrap border-b border-slate-100 px-3.5 py-2.5 text-left text-xs font-semibold text-slate-500";
export const td = "whitespace-nowrap border-b border-slate-50 px-3.5 py-3 text-sm";
